import {
  IdentificationCard,
  LinkedinLogo,
  MapPin,
} from "@phosphor-icons/react";
import { useMemo } from "react";

import { DataTable } from "common/components/DataTable";
import useDataTable from "common/components/DataTable/useDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";

import type { ColumnDef } from "@tanstack/react-table";
import type { NavigatorLead } from "../../../types";

interface NavigatorTableProps {
  leads: NavigatorLead[];
  isLoading: boolean;
  pageSize: number;
  totalItemCount: number;
}

export default function NavigatorTable({
  leads,
  isLoading,
  pageSize,
  totalItemCount,
}: NavigatorTableProps) {
  const columns = useMemo<ColumnDef<NavigatorLead>[]>(
    () => [
      {
        id: "lead",
        header: "Leads",
        cell: ({ row: { original } }) => {
          if (isLoading) {
            return (
              <div className="flex items-center gap-x-2.5">
                <Skeleton className="size-8 rounded-full" />
                <Skeleton className="h-4 w-28" />
              </div>
            );
          }

          const { picture, full_name, premium } = original;

          return (
            <div className="flex items-center gap-x-2.5">
              <ProfileImage src={picture} size="sm" />
              <span className="line-clamp-1 break-all text-xs-bold">
                {full_name}
              </span>
              {premium && (
                <LinkedinLogo
                  className="size-4 text-yellow-500"
                  weight="fill"
                />
              )}
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "title",
        header: "Title",
        cell: ({ row: { original } }) =>
          isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="line-clamp-1 break-all">{original.title}</span>
          ),
        meta: { icon: IdentificationCard, className: "min-w-40 max-w-40" },
      },
      {
        id: "company",
        header: "Company",
        cell: ({ row: { original } }) =>
          isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="line-clamp-1 break-all">
              {original.company_name}
            </span>
          ),
        meta: { icon: MapPin, className: "min-w-40 max-w-40" },
      },
    ],
    [isLoading],
  );

  // leads are only previewed here, so no selection is passed
  const table = useDataTable({
    data: leads || new Array(pageSize).fill({}),
    columns,
  });

  return (
    <DataTable
      table={table}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
    />
  );
}
